/**
 * PlanContext
 *
 * React Context for managing user's learning plan:
 * - Add/remove methods to plan
 * - Persist to localStorage (v2 format)
 * - Cross-tab sync
 * - Export to PDF
 */

import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import type { Method, PlanItem, StoredPlan } from '../types';
import { useLanguage } from './LanguageContext';

export interface ToastNotification {
  message: string;
  type: 'success' | 'error';
}

interface PlanContextType {
  plan: PlanItem[];
  addMethod: (method: Method) => void;
  removeMethod: (methodId: number) => void;
  clearPlan: () => void;
  exportPdf: () => void;
  setPlan: (methods: PlanItem[]) => void;
  toast: ToastNotification | null;
  showToast: (message: string, type: 'success' | 'error') => void;
}

const PlanContext = createContext<PlanContextType | undefined>(undefined);

interface PlanProviderProps {
  children: React.ReactNode;
}

const STORAGE_KEY = 'gf_plan';
const CURRENT_VERSION = 2;

export const PlanProvider: React.FC<PlanProviderProps> = ({ children }) => {
  const { language } = useLanguage();
  const [planData, setPlanDataState] = useState<PlanItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [toast, setToast] = useState<ToastNotification | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: StoredPlan = JSON.parse(stored);

        // Check version
        if (parsed.version === CURRENT_VERSION) {
          setPlanDataState(parsed.items || []);
        } else {
          // Migrate or reset old version data
          const newPlan: StoredPlan = {
            version: CURRENT_VERSION,
            items: [],
            timestamp: Date.now(),
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newPlan));
          setPlanDataState([]);
        }
      }
    } catch (error) {
      console.error('Error loading plan from localStorage:', error);
    } finally {
      setIsInitialized(true);
    }
  }, []);

  // Save to localStorage whenever planData changes (after initialization)
  useEffect(() => {
    if (!isInitialized) return;

    try {
      const dataToStore: StoredPlan = {
        version: CURRENT_VERSION,
        items: planData,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Error saving plan to localStorage:', error);
    }
  }, [planData, isInitialized]);

  // Listen for storage events (cross-tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const parsed: StoredPlan = JSON.parse(e.newValue);
          if (parsed.version === CURRENT_VERSION) {
            setPlanDataState(parsed.items || []);
          }
        } catch (error) {
          console.error('Error parsing storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Show toast notification, auto-clear after 3 seconds
  const showToast = useCallback((message: string, type: 'success' | 'error'): void => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  }, []);

  // Update plan data
  const setPlanData = useCallback((newPlan: PlanItem[]): void => {
    setPlanDataState(newPlan);
  }, []);

  // Add method to plan
  const addMethod = useCallback(
    (method: Method): void => {
      const newPlanItem: PlanItem = {
        id: method.id,
        title: method.title,
        duration_min: method.duration_min,
        duration_max: method.duration_max,
        facilitator_material_url: method.facilitator_material_url ?? null,
      };

      // Check if already in plan
      const exists = planData.some((item) => item.id === method.id);
      if (exists) {
        return;
      }

      const updatedPlan = [...planData, newPlanItem];
      setPlanData(updatedPlan);
    },
    [planData, setPlanData]
  );

  // Remove method from plan
  const removeMethod = useCallback(
    (methodId: number): void => {
      const updatedPlan = planData.filter((item) => item.id !== methodId);
      setPlanData(updatedPlan);
    },
    [planData, setPlanData]
  );

  // Clear plan
  const clearPlan = useCallback((): void => {
    setPlanData([]);
  }, [setPlanData]);

  // Export PDF
  const exportPdf = useCallback((): void => {
    if (planData.length === 0) {
      showToast('Your plan is empty. Add methods before exporting.', 'error');
      return;
    }

    // Use live React language state (not stale page-load tpSettings value)
    const currentLang = language;

    // Get admin-post URL from tpSettings (passed by PHP via wp_localize_script)
    const adminPostUrl = window.tpSettings?.adminPostUrl ?? '/wp-admin/admin-post.php';

    // Build form data with correct parameters
    const params = new URLSearchParams({
      action: 'gf_generate_pdf', // Correct action name (matches PHP handler)
      _wpnonce: window.tpNonce?.pdf || '', // Required nonce for security
      method_ids: planData.map((m) => m.id).join(','), // Comma-separated IDs (not JSON)
      lang: currentLang,
    });

    // Submit to WordPress admin-post.php
    fetch(adminPostUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    })
      .then(async (response) => {
        // Check Content-Type to verify we actually received a PDF
        const contentType = response.headers.get('content-type');

        if (!response.ok) {
          // Try to get error message from response body
          const text = await response.text();

          // Handle specific error codes
          if (response.status === 403) {
            throw new Error('Security check failed. Please refresh the page and try again.');
          }
          if (response.status === 400) {
            throw new Error('Invalid request. Please check your plan and try again.');
          }
          if (response.status === 404) {
            throw new Error('PDF generator not found. Please contact support.');
          }

          // Log error details for debugging
          console.error('PDF export failed:', {
            status: response.status,
            contentType,
            body: text.substring(0, 500),
          });

          throw new Error('Failed to generate PDF. Please try again or contact support.');
        }

        // Verify response is actually a PDF (not HTML error page)
        if (!contentType || !contentType.includes('application/pdf')) {
          // Server returned non-PDF content (likely an HTML error page)
          const text = await response.text();
          console.error('PDF export returned non-PDF content:', {
            contentType,
            body: text.substring(0, 500),
          });
          throw new Error('Server returned an invalid response. Please try again or contact support.');
        }

        return response.blob();
      })
      .then((blob) => {
        // Verify blob is not empty and has reasonable size
        if (!blob || blob.size < 100) {
          throw new Error('Generated PDF is empty or invalid.');
        }

        // Download PDF
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'learning-plan.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        // Track PDF export event
        window.goatcounter?.count({ path: 'pdf-export-plan', title: 'Plan PDF Export', event: true });
      })
      .catch((error: Error) => {
        console.error('PDF export error:', error);
        showToast(error.message || 'Failed to export PDF. Please try again.', 'error');
      });
  }, [planData, showToast]);

  // Set plan (for programmatic updates)
  const setPlan = useCallback(
    (methods: PlanItem[]): void => {
      setPlanData(methods);
    },
    [setPlanData]
  );

  const contextValue: PlanContextType = {
    plan: planData,
    addMethod,
    removeMethod,
    clearPlan,
    exportPdf,
    setPlan,
    toast,
    showToast,
  };

  return (
    <PlanContext.Provider value={contextValue}>{children}</PlanContext.Provider>
  );
};

/**
 * Custom hook to access PlanContext
 */
export const usePlan = (): PlanContextType => {
  const context = useContext(PlanContext);

  if (context === undefined) {
    throw new Error('usePlan must be used within a PlanProvider');
  }

  return context;
};
